import React, { useState } from 'react';
import { ButtonRounded, StatusMessage } from 'is24-corecss';
import { IconS24ArrowLeft24, IconS24Close24 } from '@is24/cosma-ui-icons';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiResponseError } from '../../api/ApiResponseError';
import { startWebIdAutoIdent } from '../../api/webid/webIdVerification';
import { withTvsState, toUrl as redirectToUrl } from '../../utils/redirect';
import { useClientConfig } from '../../utils/clientConfigContext';
import { useTvsState } from '../../utils/useTvsState';
import IdentificationInfoContent from '../../components/identificationInfoContent/IdentificationInfoContent';

const WebIdVerificationStartPage: React.FC = () => {
  const { t } = useTranslation();
  const { baseUrl, userId } = useClientConfig();
  const [, setTvsState] = useTvsState();
  const [searchParams] = useSearchParams();
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [requestError, setRequestError] = useState('');
  const sessionId = searchParams.get('tvs_session_id');

  const onStartClick = async () => {
    if (!sessionId) {
      setRequestError(t('errors.webid.bad.request' as any));
      return;
    }

    setIsRequestPending(true);
    setRequestError('');

    try {
      const response = await startWebIdAutoIdent(
        {
          sessionId,
          webIdTermsConfirmed: true,
          redirectUrl: withTvsState(baseUrl, 'webid_verification_check_page', { tvs_session_id: sessionId }),
          redirectDeclineUrl: withTvsState(baseUrl, 'verification_failure_webid', { tvs_session_id: sessionId }),
        },
        userId,
      );
      redirectToUrl(response.redirectUrl);
    } catch (error) {
      if (error instanceof ApiResponseError && error.responseStatus === 400) {
        setRequestError(t('errors.webid.bad.request' as any));
      } else if (error instanceof ApiResponseError && error.responseStatus === 401) {
        setRequestError(t('errors.unauthorized'));
      } else {
        setRequestError(t('errors.default'));
      }
    } finally {
      setIsRequestPending(false);
    }
  };

  const goBackToLandingPage = () => {
    setTvsState('verification_landing_page');
  };

  const onClose = () => {
    setTvsState('');
  };

  return (
    <div aria-modal='true' className='modal-backdrop' style={{ zIndex: 2000 }}>
      <div className='modal-container' style={{ width: 601 }}>
        <div className='modal-title-wrapper'>
          <h4 className='modal-headline grid-flex'>
            <IconS24ArrowLeft24
              onClick={goBackToLandingPage}
              className='align-center grid-align-center margin-right-xxl'
            />
            {t('WebIdVerificationStartPage.heading')}
          </h4>
          <IconS24Close24 onClick={onClose} data-testid='modal-close-icon' className='tvs-close-button' />
        </div>
        <div className='modal-content-wrapper display-flex margin-top-m'>
          <div className='modal-content palm-display-flex'>
            {requestError && (
              <StatusMessage
                data-testid='error-message'
                className='margin-bottom-xxl'
                headline={t('VerificationLandingPage.errorHeading')}
                message={requestError}
                statusType='error'
              />
            )}
            <div className='grid grid-flex margin-bottom-m'>
              <IdentificationInfoContent informationText={'WebIdVerificationStartPage.body'} />
            </div>
            <div className='margin-top-xl'>
              <div className='font-heading-xsmall-bold'>{t('WebIdVerificationStartPage.nextHeading')}</div>
              <ul style={{ listStyleType: 'disc' }} className='padding-left-l padding-bottom-xl'>
                <li className='margin-top-m font-h5'>{t('WebIdVerificationStartPage.nextStepBrowser')}</li>
                <li className='margin-top-s font-h5'>{t('WebIdVerificationStartPage.nextStepDocument')}</li>
                <li className='margin-top-s font-h5'>{t('WebIdVerificationStartPage.nextStepDuration')}</li>
              </ul>
            </div>
            <div className='grid grid-flex font-body-small-regular  font-lightgray   margin-top-xxl palm-margin-top-xl  palm-margin-bottom-l'>
              {t('WebIdVerificationStartPage.redirectWarning')}
            </div>
          </div>
        </div>
        <div className='modal-footer verification-modal-footer'>
          <div className='grid grid-flex'>
            <div className='grid-item one-whole palm-one-whole'>
              <div className='grid-flex grid-justify-end verification-footer-div palm-one-whole'>
                <ButtonRounded
                  data-testid='webid-start-verification-button'
                  className='verification-footer-button'
                  loading={isRequestPending}
                  label={t('WebIdVerificationStartPage.buttonText')}
                  size='medium'
                  onClick={onStartClick}
                  disabled={!sessionId}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebIdVerificationStartPage;
